// src/lib/macros.ts
import { getSupabase, currentUser } from "@/lib/supabase";

export interface MacroTotals {
  cal: number;
  pro: number;
  carb: number;
  fat: number;
}

export interface DayMacros extends MacroTotals {
  day: string;
}

export interface WeeklyMacrosResult {
  weekData: DayMacros[];
  today: MacroTotals;
}

const DEFAULT_GOALS: MacroTotals = {
  cal: 0,
  pro: 0,
  carb: 0,
  fat: 0,
};

/**
 * Load the last 7 days of macro data plus today's goals
 */
export async function loadWeeklyMacros(): Promise<WeeklyMacrosResult> {
  const supa = getSupabase();
  const u = await currentUser();

  // Return empty defaults if no user (consistent return type)
  if (!u) {
    console.warn("[macros] No authenticated user found");
    return { weekData: [], today: DEFAULT_GOALS };
  }

  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 6);

  // Zero out time components for accurate day boundaries
  startDate.setHours(0, 0, 0, 0);
  
  const startStr = startDate.toISOString();
  const endStr = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1).toISOString();

  console.log("[macros] Querying from:", startStr, "to:", endStr);

  const { data: entries, error } = await supa
    .from("daily_calorie_macro_logs")
    .select("created_date, daily_total_calories, daily_total_protein_grams, daily_total_carb_grams, daily_total_fat_grams")
    .eq("user_id", u.id)
    .gte("created_date", startStr)
    .lt("created_date", endStr);

  if (error) {
    console.error("[macros] Supabase error:", error.message);
    return { weekData: [], today: DEFAULT_GOALS };
  }

  console.log("[macros] Raw entries:", entries?.length || 0);

  // Aggregate by day
  const dailyTotals: Record<string, MacroTotals> = {};
  
  entries?.forEach((e) => {
    const date = new Date(e.created_date);
    const key = date.toISOString().split("T")[0]; // "2025-11-14"
    
    if (!dailyTotals[key]) {
      dailyTotals[key] = { cal: 0, pro: 0, carb: 0, fat: 0 };
    }
    
    dailyTotals[key].cal += e.daily_total_calories ?? 0;
    dailyTotals[key].pro += e.daily_total_protein_grams ?? 0;
    dailyTotals[key].carb += e.daily_total_carb_grams ?? 0;
    dailyTotals[key].fat += e.daily_total_fat_grams ?? 0;
  });

  console.log("[macros] Daily totals:", dailyTotals);

  // Build week array (oldest to newest)
  const weekData: DayMacros[] = [];
  
  for (let i = 6; i >= 0; i--) {
    // Clone endDate to avoid mutation bugs
    const date = new Date(endDate);
    date.setDate(endDate.getDate() - i);
    
    const key = date.toISOString().split("T")[0];
    const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
    const totals = dailyTotals[key] || { cal: 0, pro: 0, carb: 0, fat: 0 };
    
    weekData.push({
      day: dayName,
      cal: Math.round(totals.cal),
      pro: Math.round(totals.pro),
      carb: Math.round(totals.carb),
      fat: Math.round(totals.fat),
    });
  }

  // Fetch goals
  const todayStr = endDate.toISOString().split("T")[0];
  
  const { data: goalData, error: goalError } = await supa
    .from("client_goal_logs")
    .select("suggested_daily_calories, suggested_daily_protein, suggested_daily_carbs, suggested_daily_fat")
    .eq("user_id", u.id)
    .lte("created_date", `${todayStr}T23:59:59`)
    .order("created_date", { ascending: false })
    .limit(1);

  if (goalError) {
    console.error("[macros] Error fetching goals:", goalError.message);
  }

  const goalRow = goalData?.[0];
  
  const today: MacroTotals = {
    cal: goalRow?.suggested_daily_calories ?? 0,
    pro: goalRow?.suggested_daily_protein ?? 0,
    carb: goalRow?.suggested_daily_carbs ?? 0,
    fat: goalRow?.suggested_daily_fat ?? 0,
  };

  return { weekData, today };
}
